import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatDate, formatFreightValueDisplay } from "../mockData";
import { formatCarrierQueueWaitingTime } from "../availableFreight.mapper";
import {
  HUB_TAB_ACCEPTED,
  HUB_TAB_AVAILABLE,
  HUB_TAB_FINISHED,
  HUB_TAB_IN_REVIEW,
  HUB_TAB_LOST,
} from "../carrierHub.mapper";
import {
  isPriceVisibilityMasked,
  isAnonymousShipperLabel,
  getAnonymousShipperBannerText,
} from "../../../util/freight/carrierPriceVisibility";

function resolveIsMyClient(freight) {
  if (typeof freight?.isMyClient === "boolean") return freight.isMyClient;
  if (typeof freight?.isAffiliateShipper === "boolean") {
    return freight.isAffiliateShipper;
  }
  // Fallback: não masked costuma ser do grupo do viewer
  if (freight?.priceVisibility?.isFromViewerGroup === true) return true;
  return false;
}

function resolveBadge(freight, isMyClient) {
  const type = String(freight?.contractingType || "").toUpperCase();
  if (freight?.contractingLabel) return freight.contractingLabel;
  if (type === "BID") return "ABERTO A LANCE";
  if (isMyClient) return "PREÇO FIXO";
  return freight?.modality || "PREÇO FIXO";
}

function resolvePrimaryCta({ activeTab, isInQueue, isMasked, isMyClient }) {
  if (activeTab === HUB_TAB_IN_REVIEW || isInQueue) {
    return { label: "Sair da fila", className: "lances-btn-leave-queue", enabled: true };
  }
  if (
    activeTab === HUB_TAB_ACCEPTED ||
    activeTab === HUB_TAB_FINISHED ||
    activeTab === HUB_TAB_LOST
  ) {
    return { label: "Ver detalhes", className: "lances-btn-more", enabled: true, outline: true };
  }
  if (isMasked) {
    return {
      label: "Somente visualização",
      className: "lances-btn-accept lances-btn-accept--muted",
      enabled: false,
    };
  }
  if (isMyClient) {
    return { label: "Aceitar carga", className: "lances-btn-accept lances-btn-accept--client", enabled: true };
  }
  return { label: "Entrar na fila", className: "lances-btn-accept lances-btn-accept--open", enabled: true };
}

export default function FreightCard({
  freight,
  isSelected,
  acceptingFreightId,
  activeTab = HUB_TAB_AVAILABLE,
  onSelect,
  onAcceptFreight,
}) {
  const isAccepting = acceptingFreightId === freight.id;
  const isInQueue = Boolean(freight.isInCarrierQueue);
  const isMasked = isPriceVisibilityMasked(freight);
  const isMyClient = resolveIsMyClient(freight);
  const badgeLabel = resolveBadge(freight, isMyClient);
  const isBidStyle =
    String(freight?.contractingType || "").toUpperCase() === "BID" ||
    (!isMyClient && !isInQueue);
  const queueWaitingTime = isInQueue ? formatCarrierQueueWaitingTime(freight) : null;
  const shipperName =
    freight.priceVisibility?.displayShipperName || freight.shipper;
  const isAnonymousShipper = isAnonymousShipperLabel(shipperName);
  const displayPrice =
    freight.priceVisibility?.displayPrice ?? freight.freightValue;
  const cta = resolvePrimaryCta({ activeTab, isInQueue, isMasked, isMyClient });

  const handlePrimaryAction = (e) => {
    e.stopPropagation();
    if (cta.outline) {
      onSelect(freight.id);
      return;
    }
    if (isMasked && !isInQueue) return;
    onAcceptFreight(freight);
  };

  return (
    <Box
      className={[
        "lances-freight-card",
        isSelected ? "lances-freight-card--selected" : "",
        isInQueue ? "lances-freight-card--candidate" : "",
        isMyClient && !isInQueue ? "lances-freight-card--client" : "",
        isBidStyle && !isInQueue ? "lances-freight-card--open" : "",
        isMasked && !isMyClient ? "lances-freight-card--external" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onSelect(freight.id)}
    >
      {isInQueue ? (
        <Box className="lances-freight-card__candidate-strip">
          Candidato a este frete
        </Box>
      ) : null}

      {isMasked && !isMyClient ? (
        <Box className="lances-freight-card__external-strip">
          Valor Anunciado — somente visualização
        </Box>
      ) : null}

      {isAnonymousShipper ? (
        <Box className="lances-freight-card__anonymous-strip">
          {getAnonymousShipperBannerText(shipperName)}
        </Box>
      ) : null}

      <Box className="lances-freight-card__header">
        <Box className="lances-freight-card__badges">
          <span
            className={[
              "lances-badge",
              isBidStyle ? "lances-badge--open" : "lances-badge--fixed",
            ].join(" ")}
          >
            {badgeLabel}
          </span>
          {isMyClient ? (
            <span className="lances-badge lances-badge--client">Seu cliente</span>
          ) : null}
        </Box>
        <Box className="lances-freight-card__header-meta">
          <Typography className="lances-freight-card__number">
            #{freight.number}
          </Typography>
          {shipperName && shipperName !== "—" && !isAnonymousShipper ? (
            <Typography className="lances-freight-card__shipper">
              {shipperName}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box className="lances-freight-card__body">
        <Box className="lances-freight-card__main">
          <Box className="lances-freight-card__route">
            <FmdGoodOutlinedIcon
              fontSize="small"
              className="lances-icon-origin"
            />
            <Typography variant="body2" fontWeight={600}>
              {freight.origin}
            </Typography>
            <ArrowForwardIcon fontSize="small" className="lances-icon-muted" />
            <FmdGoodOutlinedIcon
              fontSize="small"
              className="lances-icon-destination"
            />
            <Typography variant="body2" fontWeight={600}>
              {freight.destination}
            </Typography>
          </Box>

          <Box className="lances-freight-card__specs">
            <Box className="lances-freight-card__spec">
              <LocalShippingOutlinedIcon fontSize="small" />
              <span>{freight.vehicleType || "—"}</span>
            </Box>
            <Box className="lances-freight-card__spec">
              <Inventory2OutlinedIcon fontSize="small" />
              <span>{freight.bodyType || "—"}</span>
            </Box>
            <Box className="lances-freight-card__spec">
              <CalendarTodayOutlinedIcon fontSize="small" />
              <span>{formatDate(freight.collectionDate)}</span>
            </Box>
            {freight.paymentTermLabel ? (
              <Box className="lances-freight-card__spec">
                <ScheduleOutlinedIcon fontSize="small" />
                <span>{freight.paymentTermLabel}</span>
              </Box>
            ) : null}
            {freight.cargoType ? (
              <Box className="lances-freight-card__spec">
                <DescriptionOutlinedIcon fontSize="small" />
                <span>{freight.cargoType}</span>
              </Box>
            ) : null}
          </Box>

          <Box className="lances-freight-card__actions">
            <Button
              variant={cta.outline ? "outlined" : "contained"}
              size="small"
              className={cta.className}
              disabled={isAccepting || !cta.enabled}
              onClick={handlePrimaryAction}
            >
              {isAccepting ? (
                <CircularProgress
                  size={16}
                  sx={{ color: isInQueue ? "#15803d" : "#fff" }}
                />
              ) : (
                cta.label
              )}
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="lances-btn-more"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(freight.id);
              }}
            >
              Ver detalhes
            </Button>
          </Box>
        </Box>

        <Box className="lances-freight-card__pricing">
          <Typography
            variant="caption"
            className="lances-freight-card__price-label"
          >
            {isMasked ? "Valor Anunciado" : "Valor total do frete"}
          </Typography>
          <Typography className="lances-freight-card__price-value">
            {formatFreightValueDisplay(displayPrice, freight.pricingType)}
          </Typography>
          {isInQueue && (
            <Box className="lances-freight-card__queue-hint-wrap">
              <Typography
                variant="caption"
                className="lances-freight-card__queue-hint"
              >
                Você está na fila aguardando contratação
              </Typography>
              {queueWaitingTime ? (
                <Typography
                  variant="caption"
                  className="lances-freight-card__queue-time"
                >
                  há {queueWaitingTime}
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>

      <Typography variant="caption" className="lances-freight-card__published">
        {freight.publishedAgo || "—"}
      </Typography>
    </Box>
  );
}
